import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { FeatureState } from '@modeso/aumico-lib-core-fe';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import Debug from 'debug';
import { of } from 'rxjs';
import { catchError, map, mergeMap } from 'rxjs/operators';
import * as fromActions from '../actions/announcements.actions';
import { AnnouncementsService } from '../services/announcements.service';

const debug = Debug('aumico:aumico-lib-core-fe:AnnouncementsEffects');

@Injectable()
export class AnnouncementsEffects {
    constructor(
        private readonly actions$: Actions,
        private readonly service: AnnouncementsService,
        private readonly store: Store<FeatureState>,
    ) {}

    /**
     * Effect for getting all announcements (admin/management view)
     */
    getAllAnnouncements$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromActions.announcementsActions.getAllAnnouncements),
            mergeMap(() => {
                debug('Getting all announcements');
                return this.service.getAllAnnouncements$().pipe(
                    map((response) => {
                        debug('Get all announcements success', response);
                        return fromActions.announcementsActions.getAllAnnouncementsSuccess({
                            payload: response,
                        });
                    }),
                    catchError((error: HttpErrorResponse) => {
                        debug('Get all announcements failed', error);
                        return of(
                            fromActions.announcementsActions.getAllAnnouncementsFailed({
                                payload: error,
                            }),
                        );
                    }),
                );
            }),
        ),
    );

    /**
     * Effect for creating a new announcement
     */
    createAnnouncement$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromActions.announcementsActions.createAnnouncement),
            mergeMap((action) => {
                debug('Creating announcement', action.payload);
                return this.service.createAnnouncement$(action.payload).pipe(
                    map((response) => {
                        debug('Create announcement success', response);
                        return fromActions.announcementsActions.createAnnouncementSuccess({
                            payload: response,
                        });
                    }),
                    catchError((error: HttpErrorResponse) => {
                        debug('Create announcement failed', error);
                        return of(
                            fromActions.announcementsActions.createAnnouncementFailed({
                                payload: error,
                            }),
                        );
                    }),
                );
            }),
        ),
    );

    /**
     * Effect for updating an existing announcement
     */
    updateAnnouncement$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromActions.announcementsActions.updateAnnouncement),
            mergeMap((action) => {
                debug('Updating announcement', action.payload);
                return this.service.updateAnnouncement$(action.payload.id, action.payload.changes).pipe(
                    map((response) => {
                        debug('Update announcement success', response);
                        return fromActions.announcementsActions.updateAnnouncementSuccess({
                            payload: response,
                        });
                    }),
                    catchError((error: HttpErrorResponse) => {
                        debug('Update announcement failed', error);
                        return of(
                            fromActions.announcementsActions.updateAnnouncementFailed({
                                payload: error,
                            }),
                        );
                    }),
                );
            }),
        ),
    );

    /**
     * Effect for deleting an announcement
     */
    deleteAnnouncement$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromActions.announcementsActions.deleteAnnouncement),
            mergeMap((action) => {
                debug('Deleting announcement', action.payload);
                return this.service.deleteAnnouncement$(action.payload).pipe(
                    map((response) => {
                        debug('Delete announcement success', response);
                        return fromActions.announcementsActions.deleteAnnouncementSuccess({
                            payload: action.payload,
                        });
                    }),
                    catchError((error: HttpErrorResponse) => {
                        debug('Delete announcement failed', error);
                        return of(
                            fromActions.announcementsActions.deleteAnnouncementFailed({
                                payload: error,
                            }),
                        );
                    }),
                );
            }),
        ),
    );

    /**
     * Effect for getting user-specific announcements
     */
    getUserAnnouncements$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromActions.announcementsActions.getUserAnnouncements),
            mergeMap(() => {
                debug('Getting user announcements');
                return this.service.getUserAnnouncements$().pipe(
                    map((response) => {
                        debug('Get user announcements success', response);
                        return fromActions.announcementsActions.getUserAnnouncementsSuccess({
                            payload: response,
                        });
                    }),
                    catchError((error: HttpErrorResponse) => {
                        debug('Get user announcements failed', error);
                        return of(
                            fromActions.announcementsActions.getUserAnnouncementsFailed({
                                payload: error,
                            }),
                        );
                    }),
                );
            }),
        ),
    );

    /**
     * Effect for dismissing an announcement for the current user
     */
    dismissAnnouncement$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromActions.announcementsActions.dismissAnnouncement),
            mergeMap((action) => {
                debug('Dismissing announcement', action.payload);
                return this.service.dismissAnnouncement$(action.payload).pipe(
                    map(() => {
                        debug('Dismiss announcement success');
                        return fromActions.announcementsActions.dismissAnnouncementSuccess();
                    }),
                    catchError((error: HttpErrorResponse) => {
                        debug('Dismiss announcement failed', error);
                        return of(
                            fromActions.announcementsActions.dismissAnnouncementFailed({
                                payload: error,
                            }),
                        );
                    }),
                );
            }),
        ),
    );
}
